import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../../../store/organization";
import { eventStore, useEventStore } from "../../../../store/event";
import { GET_FINANCIAL_HOURLY, GET_PRODUCT_MIX } from "./queries";
import { processData } from "./helpers";
import {
  FinancialHourlyRow,
  ProductMixRow,
  ProcessedReport,
} from "./types";
import { EventStatsBar } from "./EventStatsBar";
import { HourlyChart } from "./HourlyChart";
import { TopProductsCard } from "./TopProductsCard";
import { ReportFilters } from "../../components/ReportFilters";

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;
const { getState: getEventState } = eventStore;

const EMPTY_REPORT: ProcessedReport = {
  eventStats: {
    totalGross: 0,
    netSales: 0,
    tips: 0,
    tax: 0,
    totalTransactions: 0,
    avgTicket: 0,
    locationCount: 0,
  },
  locations: [],
  products: [],
  eventName: "",
};

export const EventSummaryReport = () => {
  const location = useLocation();
  const currentOrgId = useOrganizationStore((s) => s.organizationId);
  const currentEventId = useEventStore((s) => s.eventId);

  const [report, setReport] = useState<ProcessedReport>(EMPTY_REPORT);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const canGenerate = !!currentOrgId && !!currentEventId;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { eventId } = getEventState();
      const organizationId = getOrgState().organizationId;

      const search = new URLSearchParams(location.search);
      const whereParam = search.get("where");
      const where: any = whereParam ? JSON.parse(whereParam) : {};

      if (organizationId) {
        where.organization_id = { _eq: Number(organizationId) };
      }
      if (eventId) {
        where.event_id = { _eq: eventId };
      }

      const [financialRes, productRes] = await Promise.all([
        client.query({
          query: GET_FINANCIAL_HOURLY,
          variables: { where },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_PRODUCT_MIX,
          variables: { where },
          fetchPolicy: "network-only",
        }),
      ]);

      const financialRows: FinancialHourlyRow[] =
        financialRes.data?.reports_financial_hourly || [];
      const productRows: ProductMixRow[] =
        productRes.data?.reports_product_mix || [];

      const processed = processData(financialRows, productRows);

      if (!mountedRef.current) return;
      setReport(processed);
      setHasGenerated(true);
    } catch (err: any) {
      console.error("Error fetching event summary report:", err);
      if (!mountedRef.current) return;
      setError(err.message || "Failed to load data");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [location.search]);

  const showResults = useMemo(
    () => hasGenerated && report.locations.length > 0,
    [hasGenerated, report.locations.length]
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Filters + Generate */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 pb-3 mb-4">
        {/* Force ReportFilters' inner grid to 2 columns (dates row / vendor+location row). */}
        <div className="[&_.grid]:!grid-cols-1 md:[&_.grid]:!grid-cols-2 lg:[&_.grid]:!grid-cols-2">
          <ReportFilters
            includeDates
            includeTime
            includeVendors
            includeLocations
            eventScopedVendors
            customQueryPaths={{
              dateFrom: "_and[0].transaction_hour",
              dateTo: "_and[1].transaction_hour",
            }}
          />
        </div>
        <div className="flex items-center justify-between pt-2">
          <div>
            {!canGenerate && (
              <span className="text-xs text-gray-400">
                Select an organization and event to generate the report.
              </span>
            )}
          </div>
          <button
            onClick={fetchData}
            disabled={!canGenerate || isLoading}
            className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm transition-colors ${
              canGenerate && !isLoading
                ? "bg-red-700 text-white hover:bg-red-800"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {isLoading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {/* Event Stats */}
      {hasGenerated && (
        <EventStatsBar stats={report.eventStats} eventName={report.eventName} />
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {/* Empty states */}
      {!isLoading && !hasGenerated && !error && (
        <div className="text-center py-20 text-gray-400">
          Select an organization and event, then click Generate Report.
        </div>
      )}

      {!isLoading && hasGenerated && report.locations.length === 0 && !error && (
        <div className="text-center py-20 text-gray-500">
          No sales data found for this event.
        </div>
      )}

      {/* Hourly chart + Top products */}
      {showResults && (
        <>
          <HourlyChart locations={report.locations} />
          <TopProductsCard products={report.products} />
        </>
      )}
    </div>
  );
};
